import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/_layout/tutorial")({
  component: TutorialPage,
  head: () => ({
    meta: [
      {
        property: "og:title",
        content: "Tutorial - Macrowave",
      },
      {
        property: "og:description",
        content: "Learn how to use Macrowave to create and share your own private radio station from your Mac.",
      },
      {
        name: "description",
        content: "Learn how to use Macrowave to create and share your own private radio station from your Mac.",
      },
    ],
  }),
});

function TutorialPage() {
  return (
    <div className="mx-auto max-w-4xl py-12 px-6">
      <div className="text-center mb-12">
        <h1 className="font-serif-600 text-4xl text-black mb-4">
          How to Use Macrowave
        </h1>
      </div>

      <div className="font-serif text-lg leading-relaxed space-y-8 text-gray-800">
        {/* Video Section */}
        <div className="space-y-4">
          <h2 className="font-serif-600 text-2xl text-black mb-4 text-center">
            Creating Your First Station
          </h2>
          
          <div className="flex justify-center">
            <video 
              controls
              className="w-full max-w-3xl rounded-lg shadow-lg"
            >
              <source src="/videos/how-to-create-a-station.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-12">
          <h2 className="font-serif-600 text-2xl text-black mb-4">
            Pro Tips
          </h2>
          
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong>For best quality:</strong> Use a stable internet connection. Wired connections provide the most reliable streaming.
            </li>
            <li>
              <strong>Battery life:</strong> Streaming uses more battery. Keep your Mac plugged in for extended sessions.
            </li>
            <li>
              <strong>Audio sources:</strong> You can switch between different audio sources without stopping your station.
            </li>
            <li>
              <strong>Viewer count:</strong> See how many people are listening to your station in real-time.
            </li>
            <li>
              <strong>Private stations:</strong> Your station link is unique and private. Only share it with people you trust.
            </li>
          </ul>
        </div>

        {/* Troubleshooting */}
        <div className="mt-12">
          <h2 className="font-serif-600 text-2xl text-black mb-4">
            Troubleshooting
          </h2>
          
          <div className="space-y-4">
            <div>
              <p className="font-serif-600">No audio from certain apps?</p>
              <p className="mt-1">
                Some apps with DRM protection (like Netflix or Apple Music) may not allow audio capture. This is a system limitation.
              </p>
            </div>
            
            <div>
              <p className="font-serif-600">Connection issues?</p>
              <p className="mt-1">
                Make sure you're on a stable internet connection. If you're behind a corporate firewall, some ports may need to be opened for WebRTC.
              </p>
            </div>
            
            <div>
              <p className="font-serif-600">High latency?</p>
              <p className="mt-1">
                Latency depends on the distance between you and your listeners. For best results, ensure both broadcaster and listeners have good internet connections.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-12 text-center border-t pt-12">
          <h2 className="font-serif-600 text-2xl text-black mb-4">
            Ready to Start Broadcasting?
          </h2>
          <p className="mb-6">
            Download Macrowave now and create your first station in seconds.
          </p>
          <a 
            href="https://apps.apple.com/app/apple-store/id6746954963?pt=123127751&ct=macrowave.co&mt=8"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <img 
              src="/images/app-store-badge.svg"
              alt="Download on the App Store"
              width="120"
              height="40"
              className="h-12"
            />
          </a>
        </div>

        {/* Support */}
        <div className="mt-12 text-center">
          <p className="text-gray-600">
            Need help? Join our{" "}
            <a 
              href="/discord"
              target="_blank"
              className="text-black hover:text-gray-600 transition-colors duration-200 underline"
            >
              Discord community
            </a>
            {" "}for support and to connect with other Macrowave users.
          </p>
        </div>
      </div>
    </div>
  );
}
